/**
 * FeatureContext.jsx
 * DecisionFlow — Tenant Feature Entitlement Context
 * data-fit LLC · S666 · Step F · S676 fix: token prop removed, auth driven by useAuth
 *
 * Calls GET /api/v1/auth/me/features once per authenticated session.
 * Exposes hasFeature(), hasPlanType(), hasConnector() hooks
 * for proactive UI gating across three axes:
 *
 *   Axis 1 — Tier features:  hasFeature("feature.tier.sso_oidc")
 *   Axis 2 — Plan packages:  hasPlanType("feature.plan.draw")
 *   Axis 3 — Connectors:     hasConnector("connector.hris.workday")
 *
 * Gate patterns (spec_feature_gate_s665.md §2.5):
 *   Pattern 1 (hidden):   nav item not rendered when !hasFeature(key)
 *   Pattern 2 (locked):   nav item rendered with lock icon + upgrade message
 *   Pattern 3 (degraded): plan creation shows message if !hasPlanType(key)
 *
 * Auth model: httpOnly session cookie (no JWT token prop).
 * FeatureProvider watches useAuth().user — fires fetch when user is non-null,
 * resets to empty entitlements on logout (user null).
 * All fetches use credentials: "include" — no Authorization header needed.
 *
 * Usage:
 *   // Wrap app at root (inside AuthProvider, alongside BrowserRouter):
 *   <AuthProvider>
 *     <FeatureProvider>
 *       <App />
 *     </FeatureProvider>
 *   </AuthProvider>
 *
 *   // In any component:
 *   const { hasFeature, hasPlanType, hasConnector, featuresLoaded } = useFeatures();
 *   if (!hasFeature("feature.tier.excel_addin")) return null;
 *
 * spec_feature_gate_s665.md §2.4 — S666 Step F — S676 cookie-auth fix
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "./AuthContext.jsx";

// ---------------------------------------------------------------------------
// Context — default values used when FeatureProvider is not mounted or
// before the first fetch completes. hasFeature defaults to false (fail-closed)
// so gates stay shut until entitlements are confirmed.
// ---------------------------------------------------------------------------

const FeatureContext = createContext({
  features:       [],
  planTypes:      [],
  connectors:     [],
  featuresLoaded: false,
  featuresError:  null,
  hasFeature:     () => false,
  hasPlanType:    () => false,
  hasConnector:   () => false,
  reloadFeatures: () => {},
});

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

/**
 * FeatureProvider — mount once at app root, inside AuthProvider.
 * No props required. Reads user from AuthContext internally.
 * Loads feature entitlements from GET /api/v1/auth/me/features
 * whenever user transitions from null → non-null (login).
 * Resets to empty entitlements when user becomes null (logout).
 */
export function FeatureProvider({ apiBase = "", children }) {
  const { user } = useAuth();

  const [features,       setFeatures]       = useState([]);
  const [planTypes,      setPlanTypes]       = useState([]);
  const [connectors,     setConnectors]      = useState([]);
  const [featuresLoaded, setFeaturesLoaded] = useState(false);
  const [featuresError,  setFeaturesError]  = useState(null);

  // Track the user_account_id we last loaded for — avoids re-fetch on re-renders
  const lastLoadedUserId = useRef(null);

  const loadFeatures = useCallback(async (currentUser) => {
    if (!currentUser) {
      // Logged out — reset to empty
      setFeatures([]);
      setPlanTypes([]);
      setConnectors([]);
      setFeaturesLoaded(false);
      setFeaturesError(null);
      lastLoadedUserId.current = null;
      return;
    }

    const userId = currentUser.user_account_id;

    // Skip if we already loaded for this user
    if (userId === lastLoadedUserId.current && featuresLoaded) {
      return;
    }

    try {
      const res = await fetch(`${apiBase}/api/v1/auth/me/features`, {
        credentials: "include", // httpOnly session cookie — no Authorization header
      });

      if (!res.ok) {
        // Non-200: treat as no entitlements — do not block the UI
        console.warn(
          `FeatureContext: /auth/me/features returned ${res.status} — defaulting to empty entitlements`
        );
        setFeatures([]);
        setPlanTypes([]);
        setConnectors([]);
        setFeaturesLoaded(true);
        setFeaturesError(`HTTP ${res.status}`);
        lastLoadedUserId.current = userId;
        return;
      }

      const data = await res.json();

      setFeatures(data.features    ?? []);
      setPlanTypes(data.plan_types  ?? []);
      setConnectors(data.connectors ?? []);
      setFeaturesLoaded(true);
      setFeaturesError(null);
      lastLoadedUserId.current = userId;
    } catch (err) {
      console.error("FeatureContext: failed to load features —", err);
      setFeatures([]);
      setPlanTypes([]);
      setConnectors([]);
      setFeaturesLoaded(true);
      setFeaturesError(err.message ?? "Network error");
      lastLoadedUserId.current = userId ?? null;
    }
  }, [apiBase, featuresLoaded]);

  // Fire when user changes (login / logout)
  useEffect(() => {
    loadFeatures(user);
  }, [user, loadFeatures]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------------------------------------------------------------------
  // Gate hooks — O(1) Set lookups
  // ---------------------------------------------------------------------------

  const featureSet   = useMemo(() => new Set(features),   [features]);
  const planTypeSet  = useMemo(() => new Set(planTypes),  [planTypes]);
  const connectorSet = useMemo(() => new Set(connectors), [connectors]);

  /**
   * hasFeature(key) — Axis 1: tier-gated platform capabilities.
   * Returns true if the tenant has full entitlement to the feature.
   * Returns false when featuresLoaded=false (fail-closed — gates stay shut while loading).
   *
   * @param {string} key  e.g. "feature.tier.sso_oidc"
   * @returns {boolean}
   */
  const hasFeature = useCallback(
    (key) => {
      if (!featuresLoaded) return false; // fail-closed while loading
      return featureSet.has(key);
    },
    [featureSet, featuresLoaded]
  );

  /**
   * hasPlanType(key) — Axis 2: plan package entitlement.
   * Returns true if the tenant's bundle includes this plan type.
   * Returns true when no plan types are loaded (backward compat — short-circuit to ALLOW).
   *
   * @param {string} key  e.g. "feature.plan.draw"
   * @returns {boolean}
   */
  const hasPlanType = useCallback(
    (key) => {
      if (!featuresLoaded) return false; // fail-closed while loading
      // If no plan types loaded at all, treat as backward-compat allow
      if (planTypeSet.size === 0) return true;
      return planTypeSet.has(key);
    },
    [planTypeSet, featuresLoaded]
  );

  /**
   * hasConnector(key) — Axis 3: integration connector entitlement.
   * Returns true if the tenant has access to this connector.
   *
   * @param {string} key  e.g. "connector.hris.workday"
   * @returns {boolean}
   */
  const hasConnector = useCallback(
    (key) => {
      if (!featuresLoaded) return false; // fail-closed while loading
      return connectorSet.has(key);
    },
    [connectorSet, featuresLoaded]
  );

  const reloadFeatures = useCallback(() => {
    lastLoadedUserId.current = null;
    loadFeatures(user);
  }, [user, loadFeatures]);

  const value = useMemo(
    () => ({
      features,
      planTypes,
      connectors,
      featuresLoaded,
      featuresError,
      hasFeature,
      hasPlanType,
      hasConnector,
      reloadFeatures,
    }),
    [
      features, planTypes, connectors,
      featuresLoaded, featuresError,
      hasFeature, hasPlanType, hasConnector,
      reloadFeatures,
    ]
  );

  return (
    <FeatureContext.Provider value={value}>
      {children}
    </FeatureContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Consumer hook
// ---------------------------------------------------------------------------

/**
 * useFeatures() — consume the feature entitlement context.
 *
 * Returns:
 *   hasFeature(key)    {fn}     — Axis 1 gate check
 *   hasPlanType(key)   {fn}     — Axis 2 gate check
 *   hasConnector(key)  {fn}     — Axis 3 gate check
 *   features           {array}  — raw feature.tier.* keys
 *   planTypes          {array}  — raw feature.plan.* keys
 *   connectors         {array}  — raw connector.* keys
 *   featuresLoaded     {bool}   — false while initial fetch in-flight
 *   featuresError      {string|null} — error message if fetch failed
 *   reloadFeatures     {fn}     — force re-fetch (e.g. after plan upgrade)
 *
 * Must be used inside <FeatureProvider>.
 */
export function useFeatures() {
  return useContext(FeatureContext);
}

export default FeatureContext;
